import { useCallback, useEffect, useState } from "react";
import { Archive, ArchiveRestore, FileDown, FileText, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, API, apiError } from "@/lib/api";
import { OpsShell } from "@/components/admin/OpsShell";
import { Btn, Card, Empty, fmtDate, Loading, Pager, Pill, T, useAdmin } from "@/components/admin/ui";

export default function AdminProposals() {
  const { me, can } = useAdmin();
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = new URLSearchParams({ page: String(page), page_size: "25" });
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/admin/proposals?${params}`);
      setD(data);
    } catch (e) {
      toast.error(apiError(e));
    }
  }, [q, page]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const act = async (id, action) => {
    if (action === "delete" && !window.confirm("Permanently delete this proposal?")) return;
    try {
      await api.post(`/admin/proposals/${id}/${action}`, { reason: `admin ${action}` });
      toast.success(`Proposal ${action}d`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <OpsShell title="Proposals" subtitle="Every proposal created across all workspaces">
      <Card
        testid="admin-proposals-panel"
        title="All proposals"
        hint={d ? `${d.total} records` : ""}
        actions={
          <div className="relative">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-2.5 top-1/2 -translate-y-1/2" />
            <input
              data-testid="admin-proposals-search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Search title"
              className={`${T.input} pl-8 w-52 py-1.5`}
            />
          </div>
        }
      >
        {!d ? (
          <Loading label="Loading proposals" />
        ) : d.items.length === 0 ? (
          <Empty label="No proposals found" hint="Try a different search term" icon={FileText} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse min-w-[960px]">
                <thead>
                  <tr>
                    <th className={T.th}>Title</th>
                    <th className={T.th}>Owner</th>
                    <th className={T.th}>Client</th>
                    <th className={T.th}>Value</th>
                    <th className={T.th}>Status</th>
                    <th className={T.th}>Shared</th>
                    <th className={T.th}>Created</th>
                    <th className={T.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {d.items.map((p) => (
                    <tr key={p.id} className={`${T.row} ${p.archived ? "opacity-55" : ""}`} data-testid={`admin-proposal-row-${p.id}`}>
                      <td className={`${T.td} font-medium text-slate-800 dark:text-slate-200`}>{p.title || "Untitled"}</td>
                      <td className={`${T.td} text-xs ${T.muted}`}>{p.user}</td>
                      <td className={T.td}>{p.client_name}</td>
                      <td className={`${T.td} ${T.num}`}>{p.amount}</td>
                      <td className={T.td}>
                        <Pill value={p.status} />
                      </td>
                      <td className={T.td}>
                        <span className={`text-[13px] ${p.sent ? "text-emerald-600 dark:text-emerald-400" : T.muted}`}>
                          {p.sent ? "Yes" : "No"}
                        </span>
                      </td>
                      <td className={`${T.td} ${T.num} ${T.muted}`}>{fmtDate(p.created_at)}</td>
                      <td className={T.td}>
                        <div className="flex gap-1.5">
                          <Btn
                            data-testid={`admin-proposal-pdf-${p.id}`}
                            onClick={() => window.open(`${API}/admin/proposals/${p.id}/export.pdf`, "_blank")}
                            className="px-2"
                            title="Download PDF"
                          >
                            <FileDown className="w-3.5 h-3.5" />
                          </Btn>
                          {can("proposals.delete") && (
                            <Btn
                              data-testid={`admin-proposal-archive-${p.id}`}
                              onClick={() => act(p.id, p.archived ? "unarchive" : "archive")}
                              className="px-2"
                              title={p.archived ? "Restore" : "Archive"}
                            >
                              {p.archived ? <ArchiveRestore className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
                            </Btn>
                          )}
                          {me?.platform_role === "super_admin" && (
                            <Btn
                              data-testid={`admin-proposal-delete-${p.id}`}
                              variant="danger"
                              onClick={() => act(p.id, "delete")}
                              className="px-2"
                              title="Delete permanently"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Btn>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pager page={d.page} pages={d.pages} total={d.total} onPage={setPage} testid="admin-proposals-pager" />
          </>
        )}
      </Card>
    </OpsShell>
  );
}
